import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

// ─── Portal Sidebar ────────────────────────────────────────────────────────────
const PortalSidebar = ({ branding, isCollapsed, setIsCollapsed, navItems }) => {
  const location = useLocation();
  const primaryColor = branding?.primaryColor || '#107570';

  const isActive = (path) => location?.pathname === path;

  return (
    <aside
      className={`fixed top-16 left-0 bottom-0 border-r z-[999] transition-all duration-300 ease-in-out flex flex-col`}
      style={{
        width: isCollapsed ? '64px' : '256px',
        backgroundColor: '#ffffff',
        borderColor: '#e2e8f0',
      }}
    >
      {/* Toggle button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-6 w-6 h-6 rounded-full flex items-center justify-center shadow-sm border transition-colors hover:bg-gray-50"
        style={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0' }}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={14} color="#6b7280" />
      </button>

      <nav className="flex-1 overflow-y-auto py-4">
        {/* Nav card */}
        {!isCollapsed ? (
          <div className="px-3">
            <div className="rounded-lg border overflow-hidden shadow-sm" style={{ borderColor: '#e2e8f0', backgroundColor: '#ffffff' }}>
              <div className="px-3 pt-3 pb-2 border-b" style={{ borderColor: '#e2e8f0' }}>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 select-none">
                  Client Portal
                </p>
              </div>
              <div className="px-2 py-2 space-y-0.5">
                {navItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md font-medium text-sm transition-colors group relative"
                    style={{
                      backgroundColor: isActive(item?.path) ? `${primaryColor}15` : 'transparent',
                      color: isActive(item?.path) ? primaryColor : '#6b7280',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive(item?.path)) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                        e.currentTarget.style.color = '#111827';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive(item?.path)) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}
                  >
                    <Icon
                      name={item?.icon}
                      size={18}
                      color={isActive(item?.path) ? primaryColor : primaryColor}
                    />
                    <span className="flex-1">{item?.label}</span>
                    {isActive(item?.path) && (
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryColor }} />
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="px-3 space-y-0.5">
            {navItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                title={item?.label}
                className="flex items-center justify-center px-3 py-2.5 rounded-md transition-colors group relative"
                style={{
                  backgroundColor: isActive(item?.path) ? `${primaryColor}15` : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isActive(item?.path)) e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item?.path)) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Icon name={item?.icon} size={20} color={primaryColor} />
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  {item?.label}
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Branding badge */}
        {!isCollapsed && (
          <div className="mt-6 mx-3">
            <div
              className="rounded-lg p-3 border"
              style={{ borderColor: `${primaryColor}25`, backgroundColor: `${primaryColor}08` }}
            >
              <div className="flex items-center gap-2 mb-1">
                {branding?.logoUrl ? (
                  <img src={branding?.logoUrl} alt={branding?.agencyName} className="h-5 w-auto object-contain" />
                ) : (
                  <div
                    className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {(branding?.agencyName || 'A')?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <span className="text-xs font-semibold truncate" style={{ color: primaryColor }}>
                  {branding?.agencyName || 'Your Agency'}
                </span>
              </div>
              <p className="text-[11px] text-gray-400">
                {branding?.customDomain || 'Client Portal'}
              </p>
            </div>
          </div>
        )}
      </nav>
    </aside>
  );
};

// ─── Portal Top Nav ────────────────────────────────────────────────────────────
const PortalTopNav = ({ branding, customerConfig, user, onSignOut, isSigningOut }) => {
  const primaryColor = branding?.primaryColor || '#107570';
  const navigate = useNavigate();

  return (
    <nav
      className="fixed top-0 left-0 right-0 h-16 border-b z-[1000] flex items-center justify-between px-4 lg:px-6 gap-4"
      style={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      {/* Logo / Brand */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {branding?.logoUrl ? (
          <img
            src={branding?.logoUrl}
            alt={branding?.agencyName}
            className="h-8 w-auto object-contain max-w-[160px]"
          />
        ) : (
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
              style={{ backgroundColor: primaryColor }}
            >
              {(branding?.agencyName || 'A')?.charAt(0)?.toUpperCase()}
            </div>
            <span className="font-semibold text-gray-900 text-sm hidden sm:block">
              {customerConfig?.portalTitle || branding?.agencyName || 'Client Portal'}
            </span>
          </div>
        )}
        {branding?.logoUrl && (
          <span className="font-semibold text-gray-900 text-sm hidden sm:block">
            {customerConfig?.portalTitle || branding?.agencyName || 'Client Portal'}
          </span>
        )}
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {customerConfig?.supportEmail && (
          <a
            href={`mailto:${customerConfig?.supportEmail}`}
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
          >
            <Icon name="Mail" size={14} />
            Support
          </a>
        )}

        {/* User avatar + email */}
        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md border" style={{ borderColor: '#e2e8f0' }}>
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
            style={{ backgroundColor: primaryColor }}
          >
            {user?.email?.charAt(0)?.toUpperCase()}
          </div>
          <span className="text-xs text-gray-600 max-w-[140px] truncate">{user?.email}</span>
        </div>

        {/* Sign out */}
        <button
          type="button"
          onClick={onSignOut}
          disabled={isSigningOut}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors hover:bg-gray-50 disabled:opacity-60"
          style={{ borderColor: '#e2e8f0', color: '#6b7280' }}
        >
          {isSigningOut ? (
            <div className="w-3 h-3 rounded-full border border-t-transparent animate-spin border-gray-400" />
          ) : (
            <Icon name="LogOut" size={13} />
          )}
          <span className="hidden sm:inline">Sign Out</span>
        </button>
      </div>
    </nav>
  );
};

// ─── PortalLayout ──────────────────────────────────────────────────────────────
const PortalLayout = ({ branding, customerConfig, children, pageTitle }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const primaryColor = branding?.primaryColor || '#107570';

  // Sync sidebar width CSS var
  useEffect(() => {
    document.documentElement?.style?.setProperty(
      '--portal-sidebar-width',
      isCollapsed ? '64px' : '256px'
    );
  }, [isCollapsed]);

  const navItems = [
    { label: 'Forms', path: '/white-label-customer-portal', icon: 'LayoutGrid' },
    { label: 'Submissions', path: '/portal-submissions-dashboard', icon: 'Inbox' },
    { label: 'Analytics', path: '/portal-analytics-dashboard', icon: 'BarChart2' },
  ];

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Sign out error:', err);
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <>
      {/* Top nav */}
      <PortalTopNav
        branding={branding}
        customerConfig={customerConfig}
        user={user}
        onSignOut={handleSignOut}
        isSigningOut={isSigningOut}
      />

      {/* Sidebar */}
      <div className="hidden lg:block">
        <PortalSidebar
          branding={branding}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          navItems={navItems}
        />
      </div>

      {/* Spacer for fixed top nav */}
      <div className="h-16" />

      {/* Main content with sidebar offset */}
      <div
        className="transition-all duration-300 ease-in-out min-h-[calc(100vh-64px)]"
        style={{
          marginLeft: typeof window !== 'undefined' && window.innerWidth >= 1024
            ? (isCollapsed ? '64px' : '256px')
            : '0',
          backgroundColor: '#f8fafc',
        }}
      >
        {children}
      </div>

      {/* Footer */}
      <div
        className="border-t transition-all duration-300 ease-in-out"
        style={{
          marginLeft: typeof window !== 'undefined' && window.innerWidth >= 1024
            ? (isCollapsed ? '64px' : '256px')
            : '0',
          backgroundColor: '#ffffff',
          borderColor: '#e2e8f0',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-xs text-gray-400">
            {customerConfig?.privacyUrl && (
              <a href={customerConfig?.privacyUrl} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
                Privacy Policy
              </a>
            )}
            {customerConfig?.termsUrl && (
              <a href={customerConfig?.termsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
                Terms of Service
              </a>
            )}
            {customerConfig?.supportEmail && (
              <a href={`mailto:${customerConfig?.supportEmail}`} className="hover:text-gray-600 transition-colors">
                Contact Support
              </a>
            )}
          </div>
          {customerConfig?.showPoweredBy !== false && (
            <p className="text-xs text-gray-400">
              {branding?.emailFooterText || `Powered by ${branding?.agencyName || 'Your Agency'}`}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default PortalLayout;
